import { useMemo, useState } from 'react';
import { CaretDown, CaretRight, CaretUp } from '@phosphor-icons/react';
import { api, type ActivityLabRow, type ActivityQuizRow, type ActivityStudyRow, type StudentReportRow } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatRelative } from '../lib/format';
import { Card, ErrorState, LoadingState, Pill, ProgressBar, SectionHeading, Spinner, cx } from '../components/ui';

type SortKey = 'name' | 'materials' | 'watched' | 'quiz' | 'labs' | 'lastActivity';

const num = (v: number | null) => (v == null ? -1 : v); // nulls sort last on desc

type View = 'student' | 'labs' | 'quizzes' | 'practice' | 'needs';

/** Follow-up signals for a student (computed client-side against "now"). */
function attentionFlags(r: StudentReportRow, now: number) {
  const days = r.lastActivityAt ? (now - new Date(r.lastActivityAt).getTime()) / 86_400_000 : Infinity;
  return {
    noActivity: r.accessStatus === 'active' && days >= 7,
    lowQuiz: r.quizzesTaken > 0 && (r.quizAvgPercent ?? 0) < 70,
    awaitingReview: r.labsAwaitingReview > 0,
  };
}
const needsFollowUp = (f: ReturnType<typeof attentionFlags>) => f.noActivity || f.lowQuiz || f.awaitingReview;

export function StudioReports() {
  const [view, setView] = useState<View>('student');
  const [courseId, setCourseId] = useState<string | null>(null);
  const coursesQ = useAsync(() => api.admin.courses(), []);
  const courses = coursesQ.data ?? [];
  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="Studio"
        title="Student performance"
        description="Accomplishment across class resources, quizzes and labs. See it by student, or by activity to spot who's behind."
      />
      <SummaryChips courseId={courseId} onNeedsAttention={() => setView('needs')} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-full max-w-3xl gap-1 rounded-xl bg-slate-100 p-1 text-sm font-semibold">
          <ViewTab active={view === 'student'} onClick={() => setView('student')} label="By student" />
          <ViewTab active={view === 'labs'} onClick={() => setView('labs')} label="Labs & projects" />
          <ViewTab active={view === 'quizzes'} onClick={() => setView('quizzes')} label="Quizzes" />
          <ViewTab active={view === 'practice'} onClick={() => setView('practice')} label="Practice Q&A" />
          <ViewTab active={view === 'needs'} onClick={() => setView('needs')} label="Needs attention" />
        </div>
        {courses.length > 1 && (
          <select
            className="input max-w-[16rem] py-1.5 text-sm"
            value={courseId ?? ''}
            onChange={(e) => setCourseId(e.target.value || null)}
            title="Filter by course"
            aria-label="Filter reports by course"
          >
            <option value="">All courses</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        )}
      </div>
      {view === 'student' ? (
        <StudentTable courseId={courseId} />
      ) : view === 'needs' ? (
        <NeedsAttention courseId={courseId} />
      ) : (
        <ActivityView type={view} courseId={courseId} />
      )}
    </div>
  );
}

// Report-home chips: at-a-glance "who needs me today?" counts, computed from the
// per-student report. The first is clickable → the Needs attention view.
function SummaryChips({ courseId, onNeedsAttention }: { courseId: string | null; onNeedsAttention: () => void }) {
  const s = useAsync(() => api.admin.studentReport(courseId), [courseId]);
  if (!s.data) return null;
  const now = Date.now();
  const active = s.data.students.filter((r) => r.accessStatus === 'active');
  const flagged = active.filter((r) => needsFollowUp(attentionFlags(r, now))).length;
  const awaiting = active.reduce((sum, r) => sum + r.labsAwaitingReview, 0);
  const noActivity = active.filter((r) => attentionFlags(r, now).noActivity).length;
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={onNeedsAttention}
        className="rounded-full bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-800 transition hover:bg-amber-100"
      >
        Needs attention: {flagged}
      </button>
      <span className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600">
        Awaiting grading: {awaiting}
      </span>
      <span className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600">
        No activity in 7 days: {noActivity}
      </span>
    </div>
  );
}

// Students with one or more follow-up signals — neutral wording, no "failure" tone.
function NeedsAttention({ courseId }: { courseId: string | null }) {
  const s = useAsync(() => api.admin.studentReport(courseId), [courseId]);
  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;
  const now = Date.now();
  const flagged = s.data.students
    .filter((r) => r.accessStatus === 'active')
    .map((r) => ({ r, f: attentionFlags(r, now) }))
    .filter(({ f }) => needsFollowUp(f));
  if (flagged.length === 0) {
    return <Card className="p-6 text-sm text-slate-500">No one needs follow-up right now — nice.</Card>;
  }
  return (
    <div className="space-y-3">
      {flagged.map(({ r, f }) => (
        <Card key={r.userId} className="p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="min-w-0">
              <span className="font-semibold text-brand-navy">{r.name}</span>
              <span className="ml-2 text-xs text-slate-400">{r.email}</span>
            </div>
            <span className="text-xs text-slate-400">
              {r.lastActivityAt ? `Last active ${formatRelative(r.lastActivityAt)}` : 'No activity yet'}
            </span>
          </div>
          <ul className="mt-2 flex flex-wrap gap-2 text-xs">
            {f.noActivity && (
              <li className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">No activity in 7+ days</li>
            )}
            {f.lowQuiz && (
              <li className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">Quiz average {r.quizAvgPercent}%</li>
            )}
            {f.awaitingReview && (
              <li className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
                {r.labsAwaitingReview} awaiting review
              </li>
            )}
          </ul>
        </Card>
      ))}
    </div>
  );
}

function ViewTab({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'flex-1 rounded-lg px-3 py-1.5 transition',
        active ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500 hover:text-brand-navy',
      )}
    >
      {label}
    </button>
  );
}

function StudentTable({ courseId }: { courseId: string | null }) {
  const s = useAsync(() => api.admin.studentReport(courseId), [courseId]);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [openId, setOpenId] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const data = (s.data?.students ?? []).filter(
      (r) => !q || r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q),
    );
    const ms = (t: string | null) => (t ? new Date(t).getTime() : 0);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      switch (sort.key) {
        case 'materials':
          return (a.materialsCompleted - b.materialsCompleted) * dir;
        case 'watched':
          return (num(a.watchedAvgPercent) - num(b.watchedAvgPercent)) * dir;
        case 'quiz':
          return (num(a.quizAvgPercent) - num(b.quizAvgPercent)) * dir;
        case 'labs':
          return (num(a.labAvgPercent) - num(b.labAvgPercent)) * dir;
        case 'lastActivity':
          return (ms(a.lastActivityAt) - ms(b.lastActivityAt)) * dir;
        default:
          return a.name.localeCompare(b.name) * dir;
      }
    });
  }, [s.data, sort, query]);

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  const toggle = (key: SortKey) =>
    setSort((cur) => ({ key, dir: cur.key === key && cur.dir === 'asc' ? 'desc' : 'asc' }));

  const Th = ({ k, label, className }: { k: SortKey; label: string; className?: string }) => (
    <th className={cx('px-3 py-2 text-left font-semibold', className)}>
      <button type="button" onClick={() => toggle(k)} className="inline-flex items-center gap-1 hover:text-brand-teal">
        {label}
        {sort.key === k &&
          (sort.dir === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />)}
      </button>
    </th>
  );

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Click a student to see exactly what they did.</p>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or email…"
          className="input max-w-[16rem] py-1.5 text-sm"
          aria-label="Search students"
        />
      </div>
      <Card className="mt-3 p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/70 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="w-8 px-3 py-2" />
                <Th k="name" label="Student" />
                <Th k="lastActivity" label="Last active" />
                <Th k="materials" label="Class resources" />
                <Th k="watched" label="Watched" />
                <Th k="quiz" label="Quizzes" />
                <Th k="labs" label="Labs & projects" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <ReportRow
                  key={r.userId}
                  r={r}
                  open={openId === r.userId}
                  onToggle={() => setOpenId((cur) => (cur === r.userId ? null : r.userId))}
                />
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                    No students match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function ReportRow({ r, open, onToggle }: { r: StudentReportRow; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className={cx('cursor-pointer align-middle transition hover:bg-slate-50/70', open && 'bg-slate-50/70')} onClick={onToggle}>
        <td className="px-3 py-3 text-slate-400">
          {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        </td>
        <td className="px-3 py-3">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-brand-navy">{r.name}</span>
            {r.accessStatus !== 'active' && (
              <Pill tone={r.accessStatus === 'pending' ? 'amber' : 'rose'}>{r.accessStatus}</Pill>
            )}
          </div>
          <div className="text-xs text-slate-400">
            {r.email}
            {r.batchName ? ` · ${r.batchName}` : ''}
          </div>
        </td>

        <td className="px-3 py-3 text-xs text-slate-500">
          {r.lastActivityAt ? formatRelative(r.lastActivityAt) : <span className="text-slate-300">—</span>}
        </td>

        <td className="px-3 py-3">
          <div className="flex items-center gap-2">
            <div className="w-24">
              <ProgressBar value={r.materialsCompleted} max={r.materialsTotal || 1} />
            </div>
            <span className="tabular-nums text-slate-600">
              {r.materialsCompleted} of {r.materialsTotal}
            </span>
          </div>
        </td>

        <td className="px-3 py-3">
          {r.recordingsTotal === 0 ? (
            <span className="text-slate-300">—</span>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-16">
                <ProgressBar value={r.watchedAvgPercent ?? 0} max={100} />
              </div>
              <span className="tabular-nums text-slate-600">{r.watchedAvgPercent ?? 0}%</span>
            </div>
          )}
        </td>

        <td className="px-3 py-3">
          {r.quizzesTaken === 0 ? (
            <span className="text-slate-300">—</span>
          ) : (
            <div className="leading-tight">
              <div className="tabular-nums text-slate-600">
                {r.quizzesTaken} of {r.quizzesTotal} completed
              </div>
              <div className="text-xs text-slate-400">
                avg{' '}
                <span
                  className={cx('font-semibold', (r.quizAvgPercent ?? 0) >= 70 ? 'text-emerald-600' : 'text-amber-600')}
                >
                  {r.quizAvgPercent}%
                </span>
              </div>
            </div>
          )}
        </td>

        <td className="px-3 py-3">
          {r.labsSubmitted === 0 ? (
            <span className="text-slate-300">—</span>
          ) : (
            <div className="leading-tight">
              <div className="tabular-nums text-slate-600">
                {r.labsSubmitted} of {r.labsTotal} submitted
              </div>
              <div className="text-xs text-slate-400">
                {r.labAvgPercent != null && (
                  <>
                    avg grade <span className="font-semibold text-brand-navy">{r.labAvgPercent}%</span> ·{' '}
                  </>
                )}
                {r.labsGraded} graded
              </div>
            </div>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="bg-slate-50/40 px-3 py-4 sm:px-6">
            <StudentDetailPanel userId={r.userId} />
          </td>
        </tr>
      )}
    </>
  );
}

function StudentDetailPanel({ userId }: { userId: string }) {
  const d = useAsync(() => api.admin.studentDetail(userId), [userId]);

  if (d.loading)
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-slate-400">
        <Spinner size={14} className="text-brand-teal" /> Loading activity…
      </div>
    );
  if (d.error || !d.data) return <ErrorState message={d.error ?? 'Failed to load'} onRetry={d.reload} />;

  const { materials, quizzes, labs, feedbackGiven, commentsPosted } = d.data;
  // A short merged timeline so the first view answers "what did they just do?"
  // without scrolling past every quiz row.
  const recent = [
    ...quizzes.map((q) => ({ at: q.lastAt, text: `${q.moduleTitle ?? q.title} quiz · ${q.bestPercent}%` })),
    ...labs.map((l) => ({
      at: l.submittedAt,
      text: `${l.title} · ${l.status === 'graded' ? `graded ${l.grade ?? 0}/${l.points}` : 'submitted'}`,
    })),
    ...feedbackGiven.map((f) => ({ at: f.createdAt, text: `Rated ${f.moduleTitle} (${f.rating}★)` })),
    ...commentsPosted.map((c) => ({ at: c.createdAt, text: `Posted in ${c.moduleTitle}` })),
  ]
    .filter((x) => x.at)
    .sort((a, b) => (a.at < b.at ? 1 : -1))
    .slice(0, 5);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2">
        <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Recent activity</h4>
        {recent.length === 0 ? (
          <Empty>No activity yet.</Empty>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {recent.map((x, i) => (
              <li key={i} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-slate-700">{x.text}</span>
                <span className="shrink-0 text-xs text-slate-400">{formatRelative(x.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <DetailCard title="Class resources completed" count={materials.length}>
        {materials.length === 0 ? (
          <Empty>No class resources completed yet.</Empty>
        ) : (
          <ul className="space-y-1.5">
            {materials.map((m) => (
              <li key={m.moduleId} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-slate-700">
                  {m.moduleTitle}
                  {m.sectionTitle && <span className="text-xs text-slate-400"> · {m.sectionTitle}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-2 tabular-nums text-xs text-slate-400">
                  {m.watchedPercent != null && (
                    <span className="text-slate-500">{m.watchedPercent}% watched</span>
                  )}
                  <span>
                    {m.done}/{m.total}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </DetailCard>

      <DetailCard title="Quizzes" count={quizzes.length}>
        {quizzes.length === 0 ? (
          <Empty>No quizzes attempted.</Empty>
        ) : (
          <ul className="space-y-1.5">
            {quizzes.map((q) => (
              <li key={q.quizId} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-slate-700">{q.moduleTitle ?? q.title}</span>
                <span className="flex shrink-0 items-center gap-2 text-xs">
                  <span className={cx('font-bold tabular-nums', q.bestPercent >= 70 ? 'text-emerald-600' : 'text-amber-600')}>
                    {q.bestPercent}%
                  </span>
                  <span className="text-slate-400">
                    {q.attempts} attempt{q.attempts > 1 ? 's' : ''} · {formatRelative(q.lastAt)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </DetailCard>

      <DetailCard title="Labs & projects" count={labs.length}>
        {labs.length === 0 ? (
          <Empty>No submissions yet.</Empty>
        ) : (
          <ul className="space-y-1.5">
            {labs.map((l, i) => (
              <li key={`${l.labId}-${i}`} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-slate-700">{l.title}</span>
                <span className="flex shrink-0 items-center gap-2 text-xs">
                  {l.status === 'graded' ? (
                    <span className="font-bold tabular-nums text-brand-navy">
                      {l.grade ?? 0}/{l.points}
                    </span>
                  ) : (
                    <Pill tone="amber">submitted</Pill>
                  )}
                  <span className="text-slate-400">{formatRelative(l.submittedAt)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </DetailCard>

      <DetailCard title="What they shared" count={feedbackGiven.length + commentsPosted.length}>
        {feedbackGiven.length === 0 && commentsPosted.length === 0 ? (
          <Empty>No ratings or discussion posts.</Empty>
        ) : (
          <ul className="space-y-2">
            {feedbackGiven.map((f) => (
              <li key={`fb-${f.moduleId}`} className="rounded-lg bg-white p-2 ring-1 ring-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="font-semibold text-amber-500">{'★'.repeat(f.rating)}{'☆'.repeat(5 - f.rating)}</span>
                  <span className="truncate">{f.moduleTitle}</span>
                  <span className="ml-auto shrink-0">{formatRelative(f.createdAt)}</span>
                </div>
                {f.comment && <p className="mt-1 whitespace-pre-wrap text-slate-700">{f.comment}</p>}
              </li>
            ))}
            {commentsPosted.map((c, i) => (
              <li key={`cm-${i}`} className="rounded-lg bg-white p-2 ring-1 ring-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="font-semibold text-brand-teal">Discussion</span>
                  <span className="truncate">{c.moduleTitle}</span>
                  <span className="ml-auto shrink-0">{formatRelative(c.createdAt)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-slate-700">{c.body}</p>
              </li>
            ))}
          </ul>
        )}
      </DetailCard>
    </div>
  );
}

// Collapsed by default (just the count) — "View all" expands the full list, so
// the detail opens compact instead of dumping 20 rows at once.
function DetailCard({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      {count > 0 ? (
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 text-left">
          <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h4>
          <span className="rounded-full bg-slate-100 px-1.5 text-xs font-semibold tabular-nums text-slate-500">{count}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-brand-teal">
            {open ? 'Hide' : 'View all'}
            {open ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
          </span>
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h4>
          <span className="rounded-full bg-slate-100 px-1.5 text-xs font-semibold tabular-nums text-slate-500">{count}</span>
        </div>
      )}
      {open && count > 0 && <div className="mt-2 text-sm">{children}</div>}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-slate-400">{children}</p>;
}

// ── Completion by activity ───────────────────────────────────────────────────

function ActivityView({ type, courseId }: { type: 'labs' | 'quizzes' | 'practice'; courseId: string | null }) {
  const s = useAsync(() => api.admin.activityReport(courseId), [courseId]);
  const [openId, setOpenId] = useState<string | null>(null);

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  const total = s.data.studentCount;
  const noun = type === 'labs' ? 'lab' : type === 'quizzes' ? 'quiz' : 'class';
  const emptyText =
    type === 'labs' ? 'No labs or projects yet.' : type === 'quizzes' ? 'No quizzes yet.' : 'No practice questions yet.';
  const rows: (ActivityLabRow | ActivityQuizRow | ActivityStudyRow)[] =
    type === 'labs' ? s.data.labs : type === 'quizzes' ? s.data.quizzes : s.data.study;

  return (
    <div className="space-y-2">
      <p className="text-sm text-slate-500">
        {total} active student{total === 1 ? '' : 's'}. Click a {noun} to see who's done it and who hasn't.
      </p>
      {rows.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-400">{emptyText}</Card>
      ) : type === 'labs' ? (
        (rows as ActivityLabRow[]).map((l) => (
          <LabCompletionRow
            key={l.labId}
            row={l}
            total={total}
            open={openId === l.labId}
            onToggle={() => setOpenId((c) => (c === l.labId ? null : l.labId))}
          />
        ))
      ) : type === 'quizzes' ? (
        (rows as ActivityQuizRow[]).map((q) => (
          <QuizCompletionRow
            key={q.quizId}
            row={q}
            total={total}
            open={openId === q.quizId}
            onToggle={() => setOpenId((c) => (c === q.quizId ? null : q.quizId))}
          />
        ))
      ) : (
        (rows as ActivityStudyRow[]).map((q) => (
          <StudyCompletionRow
            key={q.moduleId}
            row={q}
            total={total}
            open={openId === q.moduleId}
            onToggle={() => setOpenId((c) => (c === q.moduleId ? null : q.moduleId))}
          />
        ))
      )}
    </div>
  );
}

function CompletionHeader({
  title,
  pill,
  sub,
  done,
  total,
  unit,
  open,
  onToggle,
}: {
  title: string;
  pill: React.ReactNode;
  sub?: string | null;
  done: number;
  total: number;
  unit: string;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-3 p-4 text-left transition hover:bg-slate-50/70"
    >
      {open ? (
        <CaretDown size={14} weight="bold" className="shrink-0 text-slate-400" />
      ) : (
        <CaretRight size={14} weight="bold" className="shrink-0 text-slate-400" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate font-semibold text-brand-navy">{title}</span>
          {pill}
        </div>
        {sub && <div className="truncate text-xs text-slate-400">{sub}</div>}
      </div>
      <div className="w-36 shrink-0 sm:w-44">
        <ProgressBar value={done} max={total || 1} />
        <div className="mt-1 text-right text-xs tabular-nums text-slate-500">
          {done} / {total} {unit}
        </div>
      </div>
    </button>
  );
}

function LabCompletionRow({
  row,
  total,
  open,
  onToggle,
}: {
  row: ActivityLabRow;
  total: number;
  open: boolean;
  onToggle: () => void;
}) {
  // Not started → Submitted (waiting for review) → Graded — three distinct states,
  // so a student who submitted evidence isn't lumped in with one who's done nothing.
  const graded = row.completed.filter((c) => c.status === 'graded' && c.grade != null);
  const awaiting = row.completed.filter((c) => !(c.status === 'graded' && c.grade != null));
  const submitted = row.completed.length;
  const pillTone = row.kind === 'project' ? 'amber' : row.kind === 'exercise' ? 'violet' : 'teal';
  const pillLabel = row.kind === 'project' ? 'Project' : row.kind === 'exercise' ? 'Exercise' : 'Lab';
  return (
    <Card className="p-0">
      <CompletionHeader
        title={row.title}
        pill={<Pill tone={pillTone}>{pillLabel}</Pill>}
        sub={[
          row.sectionTitle ? `${row.sectionTitle} · ${row.points} pts` : `${row.points} pts`,
          awaiting.length > 0 ? `${graded.length} graded · ${awaiting.length} awaiting review` : `${graded.length} graded`,
        ].join(' · ')}
        done={submitted}
        total={total}
        unit="submitted"
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="grid gap-5 border-t border-slate-100 bg-slate-50/50 p-4 sm:grid-cols-3">
          <Roster
            title={`Graded · ${graded.length}`}
            people={graded.map((c) => ({ name: c.name, right: c.grade != null ? String(c.grade) : '', graded: true }))}
            emptyText="None graded yet."
          />
          <Roster
            title={`Waiting for review · ${awaiting.length}`}
            people={awaiting.map((c) => ({ name: c.name, right: 'submitted' }))}
            emptyText="Nothing awaiting review."
          />
          <Roster
            title={`Not submitted · ${row.pending.length}`}
            people={row.pending.map((p) => ({ name: p.name }))}
            muted
            emptyText="Everyone submitted."
          />
        </div>
      )}
    </Card>
  );
}

function QuizCompletionRow({
  row,
  total,
  open,
  onToggle,
}: {
  row: ActivityQuizRow;
  total: number;
  open: boolean;
  onToggle: () => void;
}) {
  const done = row.taken.length;
  return (
    <Card className="p-0">
      <CompletionHeader
        title={row.moduleTitle ?? row.title}
        pill={<Pill tone="violet">Quiz</Pill>}
        done={done}
        total={total}
        unit="attempted"
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="grid gap-5 border-t border-slate-100 bg-slate-50/50 p-4 sm:grid-cols-2">
          <Roster
            title={`Attempted · ${done}`}
            people={row.taken.map((t) => ({
              name: t.name,
              right: `${t.bestPercent}%`,
              graded: t.bestPercent >= 70,
            }))}
            emptyText="Nobody yet."
          />
          <Roster
            title={`Not yet · ${row.notTaken.length}`}
            people={row.notTaken.map((p) => ({ name: p.name }))}
            muted
            emptyText="Everyone's attempted it."
          />
        </div>
      )}
    </Card>
  );
}

function StudyCompletionRow({
  row,
  total,
  open,
  onToggle,
}: {
  row: ActivityStudyRow;
  total: number;
  open: boolean;
  onToggle: () => void;
}) {
  const done = row.practiced.length;
  const qLabel = `${row.questionCount} question${row.questionCount === 1 ? '' : 's'}`;
  return (
    <Card className="p-0">
      <CompletionHeader
        title={row.title}
        pill={<Pill tone="teal">Practice</Pill>}
        sub={row.sectionTitle ? `${row.sectionTitle} · ${qLabel}` : qLabel}
        done={done}
        total={total}
        unit="answered"
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="grid gap-5 border-t border-slate-100 bg-slate-50/50 p-4 sm:grid-cols-2">
          <Roster
            title={`Answered at least one · ${done}`}
            people={row.practiced.map((p) => ({
              name: p.name,
              right: `${p.questionsAnswered}/${row.questionCount}`,
              graded: p.questionsAnswered >= row.questionCount,
            }))}
            emptyText="Nobody yet."
          />
          <Roster
            title={`No answer saved yet · ${row.notPracticed.length}`}
            people={row.notPracticed.map((p) => ({ name: p.name }))}
            muted
            emptyText="Everyone has answered at least one."
          />
        </div>
      )}
    </Card>
  );
}

function nameInitials(name: string) {
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

function Roster({
  title,
  people,
  muted,
  emptyText,
}: {
  title: string;
  people: { name: string; right?: string; graded?: boolean }[];
  muted?: boolean;
  emptyText: string;
}) {
  return (
    <div>
      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">{title}</h4>
      {people.length === 0 ? (
        <p className="text-sm text-slate-400">{emptyText}</p>
      ) : (
        <ul className="space-y-1.5">
          {people.map((p, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span
                className={cx(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                  muted ? 'border border-dashed border-slate-300' : 'bg-teal-50 text-brand-teal',
                )}
              >
                {muted ? '' : nameInitials(p.name)}
              </span>
              <span className={cx('truncate', muted ? 'text-slate-500' : 'text-slate-700')}>{p.name}</span>
              {p.right && (
                <span
                  className={cx(
                    'ml-auto shrink-0 font-semibold tabular-nums',
                    p.graded ? 'text-emerald-600' : 'text-slate-400',
                  )}
                >
                  {p.right}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
